import React, { useCallback, useMemo, useState } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { SectionHeader } from "../../layout/sectionHeader";
import { useSelectSelectedPlannedWorkShift } from "../../../hooks/workShiftPlanning/useSelectPlannedWorkShifts";
import { useTranslation } from "react-i18next";
import { CircularProgress, IconButton } from "@mui/material";
import { Close } from "@mui/icons-material";
import { setSidebarData } from "../../../store/workShiftPlanningSlice";
import { useAppDispatch } from "../../../framework/customStore";
import { WorkShiftPlanningSidebarContent } from "./WorkShiftPlanningSidebarContent";
import { WorkShiftDto } from "../../../models/workShift/workShift";
import { useFetch } from "../../../hooks/useFetch";
import { getWorkShiftsByTimeRange } from "../../../services/workShiftService";
import { Base } from "../../../framework/base";
import { getPlannedWorkShifts } from "../../../services/plannedWorkShiftService";
import {
    PlannedWorkShiftDto,
    PlannedWorkShiftState,
} from "../../../models/plannedWorkShift/plannedWorkShift";

export const WorkShiftPlanningSidebar = () => {
    const { t } = useTranslation();
    const shift = useSelectSelectedPlannedWorkShift();
    const [actualShifts, setActualShifts] = useState<WorkShiftDto[]>();
    const [plannedShifts, setPlannedShifts] = useState<PlannedWorkShiftDto[]>();
    const dispatch = useAppDispatch();

    const publishedShifts = useMemo(
        // Exclude currently selected shift from planned shifts.
        // Only include published shifts for time calculations.
        () =>
            plannedShifts?.filter(
                (p) =>
                    p.id !== shift?.id &&
                    p.state === PlannedWorkShiftState.Published
            ),
        [plannedShifts, shift?.employeeId]
    );

    const closeSidebar = useCallback(() => {
        dispatch(setSidebarData({ plannedShiftId: null }));
    }, [dispatch]);


    useFetch(
        (signal) => {
            if (!!shift?.employeeId && !!shift?.startTime && !!shift?.endTime) {
                return getWorkShiftsByTimeRange(
                    shift.employeeId,
                    Base.dayjsToJsonDate(Base.dateAddDays(shift.startTime, -1)),
                    Base.dayjsToJsonDate(Base.dateAddDays(shift.endTime, 1)),
                    true,
                    signal
                );
            }
        },
        {
            withLoadingIndicator: false,
            onSuccess: setActualShifts,
        },
        [shift?.employeeId, shift?.startTime, shift?.endTime]
    );

    useFetch(
        (signal) => {
            if (!!shift?.employeeId && !!shift?.startTime && !!shift?.endTime) {
                // Get planned shifts for employee where
                // shift end is on previous day or after than current shift
                // shift start is on next day or before than current shift
                return getPlannedWorkShifts(
                    {
                        employeeIds: [shift.employeeId],
                        startDate: Base.dayjsToJsonDate(
                            Base.dateAddDays(shift.startTime, -1)
                        ),
                        endDate: Base.dayjsToJsonDate(
                            Base.dateAddDays(shift.endTime, 1)
                        ),
                        includeUnassigned: false,
                    },
                    signal
                );
            }
        },
        {
            withLoadingIndicator: false,
            onSuccess: setPlannedShifts,
        },
        [shift?.employeeId, shift?.startTime, shift?.endTime]
    );

    if (!shift)
        return null;

    return (
        <Box width={375} height="100%" display="flex" flexDirection="column">
            <SectionHeader light position="sticky">
                <Box
                    width="100%"
                    display="flex"
                    flexDirection="row"
                    justifyContent="space-between"
                    alignItems="center"
                >
                    <Typography variant="h3" color="primary.dark" mb={0}>
                        {t("workShiftPlanning.sidebar.workShiftInfo")}
                    </Typography>
                    <IconButton onClick={closeSidebar}>
                        <Close />
                    </IconButton>
                </Box>
            </SectionHeader>

            <Box sx={{ overflowY: "auto", py: 2, px: 3 }}>
                {!actualShifts || !publishedShifts ? (
                    <Box display="flex" justifyContent="center" pt={3}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <WorkShiftPlanningSidebarContent
                        shift={shift}
                        actualShifts={actualShifts}
                        plannedShifts={publishedShifts}
                    />
                )}
            </Box>
        </Box>
    );
};
